
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ComplianceViolation } from '@/hooks/useSecurityCompliance';
import type { SecurityFilters } from '@/hooks/useSecurityFilters';

interface ViolationsTabProps {
  violations: ComplianceViolation[];
  filters: SecurityFilters;
  onUpdateFilter: (key: keyof SecurityFilters, value: string) => void;
  onUpdateViolationStatus: (violationId: string, status: 'open' | 'investigating' | 'resolved' | 'dismissed') => void;
  loading: boolean;
}

const ViolationsTab = ({ 
  violations, 
  filters, 
  onUpdateFilter, 
  onUpdateViolationStatus, 
  loading 
}: ViolationsTabProps) => {
  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, any> = {
      minor: 'outline',
      moderate: 'secondary',
      severe: 'default',
      critical: 'destructive'
    };

    const colors: Record<string, string> = {
      minor: 'text-green-600',
      moderate: 'text-black',
      severe: 'text-black',
      critical: 'text-red-600'
    };

    return (
      <Badge variant={variants[severity]} className={colors[severity]}>
        {severity.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      open: 'destructive',
      investigating: 'default',
      resolved: 'outline',
      dismissed: 'secondary'
    };

    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const filteredViolations = violations.filter(violation => {
    if (filters.status !== 'all' && violation.status !== filters.status) return false;
    if (filters.contentType !== 'all' && violation.content_type !== filters.contentType) return false;
    if (filters.searchTerm && !violation.violation_type.toLowerCase().includes(filters.searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Select
          value={filters.status}
          onValueChange={(value) => onUpdateFilter('status', value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="investigating">Investigating</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.contentType}
          onValueChange={(value) => onUpdateFilter('contentType', value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by content type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="node">Nodes</SelectItem>
            <SelectItem value="comment">Comments</SelectItem>
            <SelectItem value="profile">Profiles</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Violation Type</TableHead>
              <TableHead>Content Type</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Detected By</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredViolations.map((violation) => (
              <TableRow key={violation.id}>
                <TableCell className="font-medium">{violation.violation_type}</TableCell>
                <TableCell className="capitalize">{violation.content_type}</TableCell>
                <TableCell>{getSeverityBadge(violation.severity)}</TableCell>
                <TableCell className="capitalize">{violation.detected_by.replace('_', ' ')}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {Math.round(violation.detection_confidence * 100)}%
                  </Badge>
                </TableCell>
                <TableCell>{getStatusBadge(violation.status)}</TableCell>
                <TableCell>
                  {violation.status === 'open' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUpdateViolationStatus(violation.id, 'investigating')}
                        disabled={loading}
                      >
                        Investigate
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onUpdateViolationStatus(violation.id, 'resolved')}
                        disabled={loading}
                      >
                        Resolve
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ViolationsTab;
